import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AccountInfo } from '../../modules/buildings-shared/model/account-info';
import { FormControlValidators } from '../../modules/buildings-shared/utilities/form-control-validators';
import { PermissionService } from '../../modules/buildings-shared/services/permission.service';
import { EncodeErrorInfo } from '../../modules/buildings-shared/model/encode-error-info';
import { SharedUtils } from '../../modules/buildings-shared/utilities/shared-utils';
import { debounceTime, Observable, of, switchMap, take } from 'rxjs';

@Component({
  selector: 'app-register',
  imports: [RouterLink, RouterLinkActive, ReactiveFormsModule, CommonModule],
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  // template: '',
  // styles: ''
})
export class RegisterComponent implements OnInit{

    accountInfo: AccountInfo;
    registerForm: FormGroup;

    nicknameBusy: EncodeErrorInfo | undefined;
    emailBusy: EncodeErrorInfo | undefined;
    passwordHidden: boolean = true;
    passwordConfirmHidden: boolean = true;

    constructor(private formBuilder: FormBuilder, private readonly permissionService: PermissionService,
            private router: Router) {
        this.accountInfo = new AccountInfo();
        this.registerForm = this.generateForm();
    }

    ngOnInit(): void {
        // this.updateBackendValidators();
    }

    onSubmit() {
        this.permissionService.setWaitingSpinner(true);
        this.emailBusy = undefined;
        this.nicknameBusy = undefined;

        this.accountInfo.nickname = this.registerForm.get('nickname')?.value;
        this.accountInfo.email = this.registerForm.get('email')?.value;
        this.accountInfo.password = this.registerForm.get('password')?.value;

        this.permissionService.signup(this.accountInfo).then((data: boolean) => {
            const registrationSucceeded: boolean = data;
            if (registrationSucceeded) {
                console.info("RegisterComponent.onSubmit() - New success signup: " + this.accountInfo.nickname);
            } else {
                console.info("RegisterComponent.onSubmit() - Signup failed for user: " + this.accountInfo.nickname);
                this.permissionService.setWaitingSpinner(false);
                this.permissionService.alert("An error occurred during registration. Please try again later.");
            }
            this.permissionService.setWaitingSpinner(false);
            this.router.navigateByUrl("registerComplete/" + registrationSucceeded + "/" + this.accountInfo.nickname);
        }).catch((error: any) => {
            const returnedError: EncodeErrorInfo = error.error;
            console.error("RegisterComponent.onSubmit() - An error occurred: " + (returnedError.code ?? (error.code ?? error.message) ) );
            this.permissionService.setWaitingSpinner(false);
            if(returnedError?.code && returnedError.code === 'nickname_busy') {
                this.nicknameBusy = returnedError;
            } else if(returnedError?.code && returnedError.code === 'email_busy') {
                this.emailBusy = returnedError;
            } else {
                this.router.navigateByUrl("registerComplete/false");
            }
            this.registerForm.controls["nickname"].updateValueAndValidity();
            this.registerForm.controls["email"].updateValueAndValidity();
        });
    }

    generateForm(): FormGroup {
        return this.formBuilder.group( {
            nickname: ['', [
                Validators.required,
                Validators.minLength(6),
                Validators.maxLength(15),
                Validators.pattern(/^[A-Za-z0-9_.-]{1,}$/),
                this.nicknameBusyValidator({nicknameBusy: true}) ], [
                this.nicknameBusyRealTimeValidator({nicknameBusyRealTime: true})
            ]],
            email: ['', [
                Validators.required,
                Validators.email,
                Validators.maxLength(50),
                this.emailBusyValidator({emailBusy: true})], [
                this.emailBusyRealTimeValidator({emailBusyRealTime: true})
            ]],
            emailConfirm: [''],
            password: ['', [
                Validators.required,
                Validators.minLength(8),
                Validators.maxLength(50),
                FormControlValidators.patternValidator( /\d/, { hasNumber: true } ),
                FormControlValidators.patternValidator( /[A-Z]/, { hasCapitalCase: true } ),
                FormControlValidators.patternValidator( /[a-z]/, { hasSmallCase: true } ),
                FormControlValidators.patternValidator( /[!@#$%^&*()_+\-=\\\[\]{};':"|,.<>?]/, { hasSpecialCharacters: true } )
            ]],
            passwordConfirm: ['']
        }, { validators: [FormControlValidators.emailMatchValidator, FormControlValidators.passwordMatchValidator] });
    }

    toLowerCase(event: any) {
        const userInput = event.target.value;
        event.target.value = userInput.toLowerCase();
    }

    private nicknameBusyValidator(error: ValidationErrors): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if ( this.nicknameBusy?.code && (control.value && this.accountInfo.nickname) ) {
                return ( this.nicknameBusy.code.match("nickname_busy") && (control.value === this.accountInfo.nickname) ) ? error : null;
            } else {
                return null;
            }
        }
    }

    private nicknameBusyRealTimeValidator(validatorError: ValidationErrors): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            if (SharedUtils.inputValueIsEmpty(control.value)) {
                return of(null);
            } else {
                return control.valueChanges.pipe(debounceTime(2500), take(1), switchMap(_ => {
                    return this.permissionService.checkNicknameAvailable(control.value).then((result: boolean) => {
                        return result ? null : validatorError;
                    }).catch((error: any) => {
                        const returnedError: EncodeErrorInfo = EncodeErrorInfo.fromJSON(error.error);
                        console.error("RegisterComponent.nicknameBusyRealTimeValidator() - An error occurred: " + (returnedError.code ?? (error.code ?? error.message) ) );
                        return validatorError;
                    });
                }));
            }
        }
    }

    private emailBusyValidator(error: ValidationErrors): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if ( this.emailBusy?.code && (control.value && this.accountInfo.email) ) {
                return ( this.emailBusy.code.match("email_busy") && (control.value === this.accountInfo.email) ) ? error : null;
            } else {
                return null;
            }
        }
    }

    private emailBusyRealTimeValidator(validatorError: ValidationErrors): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            if (SharedUtils.inputValueIsEmpty(control.value)) {
                return of(null);
            } else {
                return control.valueChanges.pipe(debounceTime(2500), take(1), switchMap(_ => {
                    return this.permissionService.checkEmailAvailable(control.value).then((result: boolean) => {
                        return result ? null : validatorError;
                    }).catch((error: any) => {
                        const returnedError: EncodeErrorInfo = EncodeErrorInfo.fromJSON(error.error);
                        console.error("RegisterComponent.emailBusyRealTimeValidator() - An error occurred: " + (returnedError.code ?? (error.code ?? error.message) ) );
                        return validatorError;
                    });
                }));
            }
        }
    }
}
